/**
 * Centralized Language Configuration
 * 
 * This is the SINGLE SOURCE OF TRUTH for all language-related configuration.
 * To add a new language:
 * 1. Add the language code to the LanguageCode type union
 * 2. Add the language configuration to LANGUAGES object
 * 3. Add it to the tables listed under "To add a new language" at the foot
 * 
 * The system will automatically:
 * - Update all language selectors
 * - Enable RTL support if configured
 * - Handle browser language detection
 * - Configure translation loading
 */

// Language code type — English and French first (Canada's official
// languages), then by number of speakers in Canada (Statistics Canada 2021
// Census, people able to speak the language, thousands). This order IS the
// order of every language picker.
export type LanguageCode =
  | "en"   // English     official
  | "fr"   // French      official
  | "es"   // Spanish     1,172
  | "zh"   // Mandarin      987 — Simplified Chinese
  | "pa"   // Punjabi       942 — Gurmukhi script
  | "ar"   // Arabic        838
  | "hi"   // Hindi         761
  | "tl"   // Tagalog       738
  | "yue"  // Cantonese     725 — Traditional Chinese, Hong Kong usage
  | "it"   // Italian       548
  | "de"   // German        419
  | "ur"   // Urdu          415
  | "pt"   // Portuguese    337
  | "ta"   // Tamil         238
  | "he"   // Hebrew        smaller than Tamil; not in that table
  | "tr";  // Turkish       smaller than Tamil; not in that table

// Language configuration interface
export interface LanguageConfig {
  code: LanguageCode;
  name: string;           // English name
  nativeName: string;     // Name in native language — what the pickers show
  direction: "ltr" | "rtl";
  enabled: boolean;       // Whether language is active
  fallback?: LanguageCode; // Fallback language if translation missing
  /**
   * The BCP 47 tag put on <html lang> and in emails, when the code alone would
   * not pick the right glyphs. Cantonese readers read Traditional characters,
   * and `lang="yue"` does not reliably select a Traditional Chinese font.
   */
  htmlLang?: string;
  dateFormat: string;     // Default date format pattern
  numberFormat: {
    decimal: string;
    thousands: string;
  };
}

// Complete language configurations — in the order above
export const LANGUAGES: Record<LanguageCode, LanguageConfig> = {
  en: {
    code: "en",
    name: "English",
    nativeName: "English",
    direction: "ltr",
    enabled: true,
    dateFormat: "MM/dd/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  fr: {
    code: "fr",
    name: "French",
    nativeName: "Français",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ",", thousands: " " },
  },
  es: {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ",", thousands: "." },
  },
  zh: {
    code: "zh",
    name: "Mandarin (Simplified Chinese)",
    nativeName: "简体中文",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "yyyy/MM/dd",
    numberFormat: { decimal: ".", thousands: "," },
  },
  pa: {
    code: "pa",
    name: "Punjabi",
    nativeName: "ਪੰਜਾਬੀ",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  ar: {
    code: "ar",
    name: "Arabic",
    nativeName: "العربية",
    direction: "rtl",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  hi: {
    code: "hi",
    name: "Hindi",
    nativeName: "हिन्दी",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  tl: {
    code: "tl",
    name: "Tagalog",
    nativeName: "Tagalog",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "MM/dd/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  yue: {
    code: "yue",
    name: "Cantonese (Traditional Chinese)",
    nativeName: "繁體中文",
    direction: "ltr",
    enabled: true,
    fallback: "zh",
    htmlLang: "zh-HK",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  it: {
    code: "it",
    name: "Italian",
    nativeName: "Italiano",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ",", thousands: "." },
  },
  de: {
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd.MM.yyyy",
    numberFormat: { decimal: ",", thousands: "." },
  },
  ur: {
    code: "ur",
    name: "Urdu",
    nativeName: "اردو",
    direction: "rtl",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  pt: {
    code: "pt",
    name: "Portuguese",
    nativeName: "Português",
    direction: "ltr",
    enabled: true,
    fallback: "es",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ",", thousands: "." },
  },
  ta: {
    code: "ta",
    name: "Tamil",
    nativeName: "தமிழ்",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  he: {
    code: "he",
    name: "Hebrew",
    nativeName: "עברית",
    direction: "rtl",
    enabled: true,
    fallback: "en",
    dateFormat: "dd/MM/yyyy",
    numberFormat: { decimal: ".", thousands: "," },
  },
  tr: {
    code: "tr",
    name: "Turkish",
    nativeName: "Türkçe",
    direction: "ltr",
    enabled: true,
    fallback: "en",
    dateFormat: "dd.MM.yyyy",
    numberFormat: { decimal: ",", thousands: "." },
  },
};

// Derived helper constants
export const LANGUAGE_CODES = Object.keys(LANGUAGES) as LanguageCode[];
/**
 * The Intl locale each language SHOWS dates in (lib/format.ts). Regions are
 * the communities these languages serve; Arabic and Urdu keep Latin digits so
 * dates read the same as the figures typed beside them. Hindi, Punjabi and
 * Tamil group large figures the Indian way (12,49,900), as their readers do.
 * Cantonese shows Traditional characters (zh-HK); Tagalog is "fil" to Intl.
 */
export const INTL_LOCALE: Record<LanguageCode, string> = {
  en: "en-CA",
  fr: "fr-CA",
  es: "es-US",
  zh: "zh-CN",
  pa: "pa-IN",
  ar: "ar-u-nu-latn",
  hi: "hi-IN",
  tl: "fil-PH",
  yue: "zh-HK",
  it: "it-IT",
  de: "de-DE",
  ur: "ur-u-nu-latn",
  pt: "pt-BR",
  ta: "ta-IN",
  he: "he-IL",
  tr: "tr-TR",
};

/**
 * The tag the browser's speech recogniser listens for (Livvy's mic). Cantonese
 * is zh-HK — spoken Cantonese, not Mandarin.
 */
export const SPEECH_LOCALE: Record<LanguageCode, string> = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
  zh: "zh-CN",
  pa: "pa-IN",
  ar: "ar-SA",
  hi: "hi-IN",
  tl: "fil-PH",
  yue: "zh-HK",
  it: "it-IT",
  de: "de-DE",
  ur: "ur-PK",
  pt: "pt-BR",
  ta: "ta-IN",
  he: "he-IL",
  tr: "tr-TR",
};

/**
 * The language Livvy's voice is told to speak, named for a text-to-speech
 * model that otherwise guesses from the script. Traditional characters guess
 * as Mandarin, so Cantonese is spelled out; Urdu and Hindi, Punjabi and
 * Hindi sound different from the same model only if it is told.
 */
export const SPOKEN_LANGUAGE: Record<LanguageCode, string> = {
  en: "English",
  fr: "Canadian French",
  es: "Spanish",
  zh: "Mandarin Chinese",
  pa: "Punjabi",
  ar: "Arabic",
  hi: "Hindi",
  tl: "Tagalog (Filipino)",
  yue: "Cantonese, as spoken in Hong Kong — never Mandarin",
  it: "Italian",
  de: "German",
  ur: "Urdu",
  pt: "Portuguese",
  ta: "Tamil",
  he: "Hebrew",
  tr: "Turkish",
};

export const ENABLED_LANGUAGES = LANGUAGE_CODES.filter(code => LANGUAGES[code].enabled);
export const RTL_LANGUAGES = LANGUAGE_CODES.filter(code => LANGUAGES[code].direction === "rtl");
export const DEFAULT_LANGUAGE: LanguageCode = "en";

// Helper functions
export function isRTL(code: LanguageCode): boolean {
  return LANGUAGES[code]?.direction === "rtl";
}

export function getLanguageName(code: LanguageCode, native = false): string {
  const config = LANGUAGES[code];
  return native ? config?.nativeName : config?.name;
}

export function getFallbackChain(code: LanguageCode): LanguageCode[] {
  const chain: LanguageCode[] = [code];
  let current = LANGUAGES[code];
  
  while (current?.fallback && !chain.includes(current.fallback)) {
    chain.push(current.fallback);
    current = LANGUAGES[current.fallback];
  }
  
  // Always include English as final fallback
  if (!chain.includes("en")) {
    chain.push("en");
  }
  
  return chain;
}

export function isValidLanguageCode(code: string): code is LanguageCode {
  return code in LANGUAGES;
}

/** What to put on <html lang> (and an email's) for a language. */
export function htmlLangFor(code: LanguageCode): string {
  return LANGUAGES[code]?.htmlLang ?? code;
}

/**
 * The supported language a BCP 47 tag means, or null — for a browser's
 * `navigator.language` and for a value a form posts or a row stores.
 * "fr-CA" → fr. Chinese splits by script: Traditional (zh-Hant, or a Hong
 * Kong, Macau or Taiwan region) → yue, everything else → zh. "fil" (Filipino,
 * what browsers report) → tl.
 */
export function languageFromTag(tag: unknown): LanguageCode | null {
  if (typeof tag !== "string") return null;
  const t = tag.trim().toLowerCase().replace(/_/g, "-");
  if (!t) return null;
  if (isValidLanguageCode(t)) return t;
  const [base, ...rest] = t.split("-");
  if (base === "zh") {
    return rest.some((p) => p === "hant" || p === "hk" || p === "mo" || p === "tw") ? "yue" : "zh";
  }
  if (base === "fil") return "tl";
  return isValidLanguageCode(base) ? base : null;
}

export function detectBrowserLanguage(): LanguageCode {
  if (typeof navigator === "undefined") return DEFAULT_LANGUAGE;
  const code = languageFromTag(navigator.language);
  return code && LANGUAGES[code].enabled ? code : DEFAULT_LANGUAGE;
}

/**
 * To add a new language:
 * 
 * 1. Add the language code to LanguageCode type union above, in its place
 *    by number of speakers in Canada
 * 2. Add the full configuration to LANGUAGES object above, and its INTL_LOCALE
 * 3. Add it to the per-language tables that cannot derive from this file:
 *    client/src/lib/dateLocale.ts (calendar names), the speech codes in
 *    client/src/components/livvy/LivvyChat.tsx, client/src/lib/pdfLanguage.ts
 *    if Windows-1252 can draw it, and the Livvy prompt names in server/ai.ts.
 *    tests/languageConfig.test.ts fails if one is missed.
 * 4. Run /translationsync — it translates every registered key into every
 *    enabled language here. No static translations file is needed.
 * 
 * Example for adding Japanese:
 * 
 * // In LanguageCode type:
 * | "ja"  // Japanese
 * 
 * // In LANGUAGES object:
 * ja: {
 *   code: "ja",
 *   name: "Japanese",
 *   nativeName: "日本語",
 *   direction: "ltr",
 *   enabled: true,
 *   fallback: "en",
 *   dateFormat: "yyyy/MM/dd",
 *   numberFormat: { decimal: ".", thousands: "," },
 * },
 * 
 * // Then /translationsync translates every key into it.
 */
